import { type NavigationGuard } from 'vue-router';
import { RouteName } from './types';
import { useAuthState } from '@/state/user';
import { firebaseAuth } from '@/api/auth/auth.api';
import {
  isSafeInternalRedirect,
  parseIncomingAppUrl,
  storePendingDeepLink,
  withExternalPostViewEntry,
} from '@/services/deep-link';

const { userAuth, setUser } = useAuthState();

export const authGuard: NavigationGuard = async (to, _from, next) => {
  let user = userAuth.value;
  if (!userAuth.value) {
    user = await getAuth();
  }

  if (to.meta.requireAuth) {
    if (!user) {
      if (isSafeInternalRedirect(to.fullPath)) {
        storePendingDeepLink(to.fullPath);
      }
      return next({
        path: `/${RouteName.Login}`,
        query: isSafeInternalRedirect(to.fullPath) ? { redirect: to.fullPath } : {},
      });
    }
  }

  if (to.meta.requireUnAuth) {
    if (user) {
      const redirect = to.query.redirect;
      const target = typeof redirect === 'string' && isSafeInternalRedirect(redirect) ? redirect : '/';
      if (target !== '/') {
        const parsed = parseIncomingAppUrl(target);
        if (parsed?.postId) {
          return next(withExternalPostViewEntry(parsed.path, parsed.query).fullPath);
        }
      }
      return next(target);
    }
  }

  next();
};

export async function getAuth() {
  // authStateReady waits for IndexedDB restore without leaving a hanging
  // onAuthStateChanged subscription if something goes wrong mid-boot.
  await firebaseAuth.authStateReady();
  const user = firebaseAuth.currentUser;
  setUser(user);
  return user;
}
